import React, { useMemo } from "react";
import type { Placement } from "@floating-ui/react";
import {
  IconButton,
  SeemPopover,
  openManagementModesEnum,
} from "@SeemplicityDev/shared";
import { filterTypes, type FilterType } from "../../../Findings/FindingsUtils";
import useFiltersData from "../../useFiltersData";
import useStatuses from "../../../Findings/FindingDetailsPane/FindingDetailsPaneHeader/Statuses/useStatuses";
import FilterDisplayByFilterToExecute from "../FilterPanel/FilterConditionsDisplay/FilterDisplayByFilterToExecute/FilterDisplayByFilterToExecute";
import { getFilterKeysEntity } from "../filterEntity";
import "./FilterPreview.scss";

export const FILTER_PREVIEW_DELAY_MS = 300;
export const FILTER_PREVIEW_CLOSE_DELAY_MS = 100;

export interface FilterPreviewPopoverProps {
  filterQuery?: string;
  filterType: FilterType;
  placement?: Placement;
}

interface FilterPreviewBodyProps {
  filterQuery: string;
  filterType: FilterType;
}

// Separate component so SeemPopover mounts the hooks only while open.
const FilterPreviewBody: React.FC<FilterPreviewBodyProps> = ({
  filterQuery,
  filterType,
}) => {
  const filterEntity = useMemo(
    () =>
      filterType === filterTypes.FILTER
        ? getFilterKeysEntity(filterQuery)
        : undefined,
    [filterQuery, filterType]
  );
  const { fieldsData, fieldsLoading } = useFiltersData({
    filterType,
    filterContext: filterEntity,
  });
  const { statuses } = useStatuses();

  return (
    <FilterDisplayByFilterToExecute
      filterType={filterType}
      filterToExecute={filterQuery}
      fieldsData={fieldsData}
      fieldsLoading={fieldsLoading}
      statusesData={statuses}
      showLegacyFilterWarning
      emptyState={
        <div
          className={"filter-preview-empty"}
          data-testid={"filter-preview-empty"}
        >
          Preview unavailable
        </div>
      }
    />
  );
};

const FilterPreviewPopover: React.FC<FilterPreviewPopoverProps> = ({
  filterQuery,
  filterType,
  placement = "bottom-end",
}) => {
  if (!filterQuery) {
    return null;
  }

  const isScope = filterType === filterTypes.SCOPE;

  return (
    <SeemPopover
      className={"filter-preview-popover"}
      triggerClassName={"filter-preview-trigger"}
      placement={placement}
      openManagementMode={openManagementModesEnum.HOVER}
      delayOpen={FILTER_PREVIEW_DELAY_MS}
      delayClose={FILTER_PREVIEW_CLOSE_DELAY_MS}
      body={
        <FilterPreviewBody filterQuery={filterQuery} filterType={filterType} />
      }
    >
      <IconButton
        buttonStyle={"secondary"}
        type={"button"}
        isIcon={"seem-icon-eye"}
        data-testid={"preview-filter-item"}
        withTooltip={{ content: isScope ? "Preview Scope" : "Preview Filter" }}
      />
    </SeemPopover>
  );
};

export default FilterPreviewPopover;
